import React, { useEffect, useState } from "react";
import { formatDate } from "@/lib/formatDate";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const Row = ({ ziffer, label, value, bold }) => (
  <div className={`flex items-center gap-3 border-b border-slate-200 py-1.5 ${bold ? "font-semibold" : ""}`}>
    <span className="w-10 text-xs text-slate-500 shrink-0">{ziffer}</span>
    <span className="flex-1 text-sm">{label}</span>
    <span className="font-mono text-sm" data-testid={`cert-ziffer-${ziffer.replace(/\./g, "-")}`}>
      {value != null ? Number(value).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ""}
    </span>
  </div>
);

export function SalaryCertificate() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const year = parseInt(searchParams.get("year") || String(new Date().getFullYear()), 10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const companyId = localStorage.getItem("demoCompanyId");

  useEffect(() => {
    if (!companyId || !employeeId) return;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const res = await apiClient.get(
          `/companies/${companyId}/employees/${employeeId}/salary-certificate`,
          { params: { year } }
        );
        setData(res.data);
      } catch (e) {
        setData(null);
        setError(e.response?.data?.detail || "Fehler beim Laden des Lohnausweises");
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId, employeeId, year]);

  const z = data?.ziffern || {};

  return (
    <div className="max-w-3xl mx-auto py-6 px-4" data-testid="salary-certificate-page">
      <div className="flex items-center justify-between mb-4 print:hidden">
        <Button variant="outline" size="sm" onClick={() => navigate("/employees")} data-testid="cert-back-button">
          Zurück
        </Button>
        <div className="flex items-center gap-2">
          <select
            className="border border-slate-200 rounded-md px-2 py-1.5 text-sm"
            value={year}
            onChange={(e) => setSearchParams({ year: e.target.value })}
            data-testid="cert-year-select"
          >
            {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <Button size="sm" onClick={() => window.print()} disabled={!data} data-testid="cert-print-button">
            Drucken / PDF
          </Button>
        </div>
      </div>

      {loading && <p className="text-sm text-slate-400" data-testid="cert-loading">Lade Lohnausweis ...</p>}
      {error && !loading && (
        <Card className="p-4 border border-rose-200 bg-rose-50 text-sm text-rose-800" data-testid="cert-error">
          {error}
        </Card>
      )}

      {data && (
        <Card className="p-8 border border-slate-300 bg-white shadow-sm print:shadow-none print:border-0" data-testid="cert-document">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-xl font-bold">Lohnausweis {data.year}</h1>
              <p className="text-xs text-slate-500 mt-1">
                Certificat de salaire · Certificato di salario<br />
                Für die Steuererklärung — Zeitraum {data.from_period} bis {data.to_period} ({data.months_count} Monat{data.months_count !== 1 ? "e" : ""})
              </p>
            </div>
            <div className="text-right text-xs text-slate-600">
              <p className="font-semibold">{data.company?.name}</p>
              {data.company?.street && <p>{data.company.street}</p>}
              {(data.company?.postal_code || data.company?.city) && (
                <p>{data.company?.postal_code} {data.company?.city}</p>
              )}
              {data.company?.uid && <p>UID: {data.company.uid}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6 text-sm border border-slate-200 rounded-md p-3 bg-slate-50 print:bg-white">
            <div>
              <p className="text-xs text-slate-400">Arbeitnehmer/in</p>
              <p className="font-medium" data-testid="cert-employee-name">
                {data.employee?.first_name} {data.employee?.last_name}
              </p>
              {data.employee?.street && <p className="text-xs">{data.employee.street}</p>}
              {(data.employee?.postal_code || data.employee?.city) && (
                <p className="text-xs">{data.employee?.postal_code} {data.employee?.city}</p>
              )}
            </div>
            <div className="text-xs space-y-1">
              {data.employee?.ahv_number && <p><span className="text-slate-400">AHV-Nr.:</span> {data.employee.ahv_number}</p>}
              {data.employee?.birth_date && <p><span className="text-slate-400">Geburtsdatum:</span> {formatDate(data.employee.birth_date)}</p>}
            </div>
          </div>

          <div className="space-y-0">
            <Row ziffer="1" label="Lohn (inkl. 13. Monatslohn und Zulagen)" value={z["1_lohn"]} />
            {z["2_2_privatanteil_geschaeftswagen"] > 0 && (
              <Row ziffer="2.2" label="Privatanteil Geschäftswagen" value={z["2_2_privatanteil_geschaeftswagen"]} />
            )}
            {z["3_unregelmaessige_leistungen"] > 0 && (
              <Row ziffer="3" label="Unregelmässige Leistungen (davon 13. Monatslohn)" value={z["3_unregelmaessige_leistungen"]} />
            )}
            <Row ziffer="8" label="Bruttolohn total" value={z["8_bruttolohn_total"]} bold />
            <Row ziffer="9" label="Beiträge AHV/IV/EO/ALV/NBUV (inkl. UVGZ/KTG)" value={-z["9_ahv_alv_nbu"]} />
            <Row ziffer="10.1" label="Berufliche Vorsorge BVG (ordentliche Beiträge)" value={-z["10_1_bvg"]} />
            <Row ziffer="11" label="Nettolohn" value={z["11_nettolohn"]} bold />
            {z["12_quellensteuer"] > 0 && (
              <Row ziffer="12" label="Quellensteuerabzug" value={z["12_quellensteuer"]} />
            )}
          </div>

          {z["15_bemerkungen"] && (
            <div className="mt-4 text-xs text-slate-600">
              <span className="text-slate-400 mr-2">15 Bemerkungen:</span>
              {z["15_bemerkungen"]}
            </div>
          )}

          <p className="mt-8 text-[10px] text-slate-400">
            Erstellt am {new Date().toLocaleDateString("de-CH")} · Ohne Unterschrift gültig, sofern elektronisch erstellt.
            Die Angaben basieren auf den erfassten Lohnläufen — bitte vor Abgabe prüfen.
          </p>
        </Card>
      )}
    </div>
  );
}
